import React, { useState, useEffect } from 'react'
import { DateRangePicker, DateRange } from 'react-date-range'
import moment from 'moment'
import classNames from 'classnames'
import { addDays } from 'date-fns'
import { isUndefined } from 'lodash'
import { IconCalendar } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { FilterPill } from '../filters/FilterPill'
import { DateRangePickerButtons } from './DateRangePickerButtons'
import { DateRangePickerClear } from './DateRangePickerClear'
import {
  customStaticRanges,
  customStaticRanges120,
  getDateRangeKey,
  getDateRangeLabel,
  getTriggerLabel,
  isValidDate,
  dateAriaLabels,
} from '../helpers'

import './DateRangePicker.scss'

/**
 * @import import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
 * @param {object} props
 * @param {Date|string|null} [props.startDate] Selected range start
 * @param {Date|string|null} [props.endDate] Selected range end
 * @param {Date|null} [props.initialStartDate] Start restored when the popup reopens or cancel resets
 * @param {Date|null} [props.initialEndDate] End restored when the popup reopens or cancel resets
 * @param {boolean} [props.error] Critical styling on the trigger; also disables the save button
 * @param {(event: object) => void} [props.onSave] Called by the save button with its click event; the pending range arrives through onChange
 * @param {() => void} [props.onClose] Called when the popup closes
 * @param {(event: object) => void} [props.onCancel] Called by the cancel button
 * @param {(event: object) => void} [props.onClear] Called by the clear button with its click event
 * @param {(value: {selection: object}) => void} props.onChange Called with { selection: { startDate, endDate, text } } on every change
 * @param {boolean} [props.withButtons] Shows save and cancel buttons in the popup
 * @param {boolean} [props.displayClearButton] Shows a clear button in the popup
 * @param {boolean} [props.withSidebar] Shows the preset ranges sidebar
 * @param {boolean} [props.allowFutureDates] Allows picking dates after today
 * @param {boolean} [props.allowPastDates] Allows picking dates before today
 * @param {boolean} [props.basicLabel] Plain-text trigger label instead of the formatted date display
 * @param {boolean} [props.fixedWidth] Caps the Button trigger at 300px instead of full width
 * @param {string} [props.saveLabel] Custom save button text
 * @param {string} [props.cancelLabel] Custom cancel button text
 * @param {string} [props.clearButtonLabel] Custom clear button text
 * @param {boolean} [props.disableSaveButton] Disables the save button
 * @param {boolean} [props.disableClearButton] Disables the clear button
 * @param {boolean} [props.shouldNotResetOnCancel] Keeps the pending selection when cancel is clicked
 * @param {boolean} [props.isFilterPill] Renders the trigger as a FilterPill
 * @param {boolean} [props.modalOpen] Closes the popup when another modal opens
 * @param {boolean} [props.friendlyLabel] Shows the preset name on the trigger when the range matches one; only applies with basicLabel
 * @param {string} [props.columnAccessor] Renders the trigger as a table header filter icon button
 * @param {boolean} [props.noValueSelected] Empty-filter tooltip and styling for the table header trigger
 * @param {React.ReactNode} [props.icon] Icon for the FilterPill trigger
 * @param {boolean} [props.showFilterLabel] Shows the Date label on the FilterPill trigger
 * @param {boolean} [props.allow120Days] 120-day preset ranges and save limit instead of 60
 * @param {boolean} [props.saveOrCloseOnClickPreset] Closes the popup when a preset range is clicked
 * @param {string|null} [props.pickedPreset] Preset key the consumer owns ('last 30 days'), null for none; omit to use the picker's memory
 */
export const DateRangePickerReusable = ({
  startDate = null,
  endDate = null,
  initialStartDate = null,
  initialEndDate = null,
  error = false,
  onSave = null,
  onClose = null,
  onCancel = null,
  onClear = null,
  withButtons = false,
  displayClearButton = false,
  withSidebar = true,
  allowFutureDates = false,
  allowPastDates = true,
  basicLabel = false,
  saveLabel = null,
  cancelLabel = null,
  clearButtonLabel = null,
  disableSaveButton = false,
  disableClearButton = false,
  shouldNotResetOnCancel = false,
  isFilterPill = false,
  modalOpen = false,
  friendlyLabel = false,
  columnAccessor = null,
  noValueSelected = false,
  icon,
  showFilterLabel = true,
  allow120Days = false,
  saveOrCloseOnClickPreset = false, // This enables the popup to close if a static range is selected.
  //  It's safe to set this to true if withButtons is false, otherwise the change and save actions
  // might be different and it needs to be handled manually. Currently this is only the case for CallSearchPills
  pickedPreset,
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [clickedPreset, setClickedPreset] = useState(null)
  const [openDateRange, setOpenDateRange] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
  })
  const inputIsInvalid = (startDate && !endDate) || (endDate && !startDate)
  const isDisabled = inputIsInvalid || disableSaveButton || error
  const DateComponent = withSidebar ? DateRangePicker : DateRange
  const maxDate = addDays(new Date(), 10000)
  const minDate = addDays(new Date(), -10000)
  const now = moment().toDate()
  // ponytail: memory dies with the picker and ignores the consumer's own date writes; such consumers pass pickedPreset.
  const preset = isUndefined(pickedPreset) ? clickedPreset : pickedPreset
  const label = getTriggerLabel(startDate, endDate, basicLabel, friendlyLabel, preset)
  const selectedRange = {
    key: 'selection',
    startDate: startDate ? moment(startDate).toDate() : null,
    endDate: endDate ? moment(endDate).toDate() : null,
    // Not `text`: react-date-range spreads this range into every onChange, and text marks a preset click.
    presetLabel: getDateRangeLabel({ startDate, endDate, dateRange: preset }),
  }

  const handleSetOpen = (newOpen) => {
    if (newOpen) {
      setOpenDateRange({
        startDate: initialStartDate || selectedRange.startDate,
        endDate: initialEndDate || selectedRange.endDate,
        clickedPreset,
      })
      setOpen(true)
    } else {
      // Close before notifying so consumers reading picker state inside onClose see it closed.
      setOpen(false)
      if (onClose) {
        onClose()
      }
    }
  }

  const handleCancel = (event) => {
    event.stopPropagation()

    if (!shouldNotResetOnCancel) {
      // The preset names the dates, so it goes back with them.
      setClickedPreset(openDateRange.clickedPreset)
      props.onChange({
        selection: {
          key: 'selection',
          startDate: openDateRange.startDate,
          endDate: openDateRange.endDate,
        },
      })
    }
    setOpen(false)
    if (onCancel) {
      onCancel(event)
    }
  }

  const handleSave = (value) => {
    onSave(value)
    setOpen(false)
  }

  const handleChange = (value) => {
    const {
      selection: { startDate, endDate, text },
    } = value
    // Only a preset click carries text, so a calendar or typed pick forgets the last preset.
    setClickedPreset(getDateRangeKey(text) ?? null)

    if (!endDate || !isValidDate(endDate)) {
      return props.onChange({ selection: { key: 'selection', startDate, endDate: null } })
    }

    if (!startDate || !isValidDate(startDate)) {
      return props.onChange({ selection: { key: 'selection', startDate: null, endDate } })
    }

    if (text && saveOrCloseOnClickPreset) {
      // The existence of "text" means the user clicked a preset range
      setOpen(false)
    }

    return props.onChange({ selection: { startDate, endDate, text } })
  }

  useEffect(() => {
    // If a different modal opens, close the date picker popup if it's already open
    if (modalOpen && open) {
      setOpen(false)
    }
  }, [modalOpen])

  const trigger = columnAccessor ? (
    <div>
      <InteractiveTooltip content={noValueSelected ? 'Filter by Date' : `Filtering: ${label}`}>
        <button
          data-testid={`${props['data-testid']}-column-accessor`}
          type="button"
          className={classNames('table-header-button', {
            'filter-applied': !noValueSelected,
          })}
        >
          <IconCalendar />
        </button>
      </InteractiveTooltip>
    </div>
  ) : isFilterPill ? (
    <FilterPill
      data-testid={`${props['data-testid'] || 'date'}-filter-pill`}
      clickable
      isClearable={false}
      label="Date"
      value={label}
      icon={icon}
      showLabel={showFilterLabel}
      active={open}
    />
  ) : (
    <Button
      fullWidth
      className={classNames('date-picker-trigger', { error, 'form-active': open })}
      data-testid={props['data-testid']}
      style={{ maxWidth: props.fixedWidth ? '300px' : '100%' }}
    >
      {label}
    </Button>
  )

  return (
    <InteractiveTooltip
      block
      clickable
      open={open}
      setOpen={handleSetOpen}
      placement="bottom-start"
      theme="light"
      className="date-range-picker-popup"
      content={
        <>
          <DateComponent
            ranges={[selectedRange]}
            maxDate={allowFutureDates ? maxDate : now}
            minDate={allowPastDates ? minDate : now}
            rangeColors={['var(--color-brand-control)']}
            editableDateInputs
            dateDisplayFormat="MM/dd/yyyy"
            staticRanges={!allow120Days ? customStaticRanges : customStaticRanges120}
            inputRanges={[]}
            startDatePlaceholder="Start Date"
            endDatePlaceholder="End Date"
            ariaLabels={dateAriaLabels}
            {...props}
            onChange={handleChange}
          />
          {displayClearButton && (
            <DateRangePickerClear
              handleClear={onClear}
              clearButtonLabel={clearButtonLabel}
              disabled={disableClearButton}
            />
          )}
          {withButtons && (
            <DateRangePickerButtons
              handleCancel={handleCancel}
              handleSave={handleSave}
              cancelLabel={cancelLabel}
              saveLabel={saveLabel}
              disabled={isDisabled}
              disabledMessage={
                isDisabled ? `Date range must be ${allow120Days ? 120 : 60} days or less` : ''
              }
            />
          )}
        </>
      }
    >
      {trigger}
    </InteractiveTooltip>
  )
}
